import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Button,
  Divider,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Timeline as ActivityIcon,
  Bolt as BoltIcon,
  Battery20 as BatteryIcon,
  Settings as SpeedIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Warning as WarningIcon,
  Power as PowerIcon
} from '@mui/icons-material';
import { ChargerService, SessionService, ApiService } from '../../services';
import { useSelector } from 'react-redux';
import { selectSelectedTeam } from '../../store/authSlice';
import { toast } from '../../utils';

const ChargerDetailsDrawer = ({ open, onClose, charger, onRefresh }) => {
  // Hooks must be called at the top level
  const selectedTeam = useSelector(selectSelectedTeam);
  const [activeSessions, setActiveSessions] = React.useState([]);
  const [selectedConnector, setSelectedConnector] = React.useState(null);
  const [rfidTags, setRfidTags] = React.useState([]);
  const [selectedTag, setSelectedTag] = React.useState(null);
  const [showTagSelection, setShowTagSelection] = React.useState(false);
  const [tagsLoading, setTagsLoading] = React.useState(false);
  
  React.useEffect(() => {
    if (charger?.connectors) {
      // Auto-select first connector if only one, otherwise no selection
      if (charger.connectors.length === 1) {
        setSelectedConnector(charger.connectors[0]);
      } else {
        setSelectedConnector(null);
      }
    }
    
    if (charger?.status === 'charging') {
      SessionService.getActiveSessions().then(response => {
        if (response.success) {
          setActiveSessions(response.data);
        }
      });
    }
  }, [charger]);

  // Early return after hooks
  if (!charger) return null;

  const handleRemoteStart = async () => {
    if (!selectedConnector) {
      toast.error('Please select a connector first');
      return;
    }
    
    if (!selectedTag) {
      toast.error('Please select an RFID tag first');
      return;
    }
    
    try {
      const chargerId = charger.chargepoint_id || charger.id;
      
      // Create payload with RFID tag
      const startPayload = {
        parameters: {
          connectorId: selectedConnector.connectorId,
          idTag: selectedTag.tagId // Use tagId from the API response
        },
        chargerId: chargerId,
        remoteOperationType: "RemoteStart",
        source: "FLEETOPS"
      };
      
      const response = await ApiService.post('/cms/v1/remote/operation', startPayload);
      
      if (response.success) {
        toast.success(`Starting connector ${selectedConnector.connectorId} with RFID tag ${selectedTag.tagId}...`);
        
        // Check operation status and update connector if successful
        await checkOperationStatusAndUpdate(chargerId, response.data?.taskId);
        
        // Refresh parent data after successful operation
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 4000); // Refresh after 4 seconds to allow operation to complete
        }
      } else {
        toast.error(response.data?.message || response.message || 'Failed to start charger');
      }
    } catch (error) {
      console.error('Error starting charger:', error);
      toast.error('Error starting charger');
    }
    
    // Reset selections after successful start
    setSelectedTag(null);
    setShowTagSelection(false);
    onClose();
  };

  const handleRemoteStop = async () => {
    if (!selectedConnector) {
      toast.error('Please select a connector first');
      return;
    }
    try {
      const chargerId = charger.chargepoint_id || charger.id;
      const chargepointPk = charger.chargepoint_pk;
      const teamId = selectedTeam?.teamId;
      
      // Ensure we have teamId before making the API call
      if (!teamId || !chargepointPk) {
        toast.error('Missing team ID or chargepoint ID');
        return;
      }
      
      
      // Always fetch active transactions to get transaction ID for stop operations
      const transactionsResponse = await ChargerService.getActiveTransactions(teamId, chargepointPk);
      
      if (transactionsResponse.success && transactionsResponse.data && Array.isArray(transactionsResponse.data.data)) {
        const transactions = transactionsResponse.data.data;
        
        // Find transaction for the selected connector
        // Filter transactions for the specific connector
        const connectorTransactions = transactions.filter(
          transaction => transaction.connectorId === selectedConnector.connectorId
        );
        
        
        if (connectorTransactions.length > 0) {
          // Use the first transaction if multiple exist (shouldn't happen normally)
          const connectorTransaction = connectorTransactions[0];
          
          if (connectorTransactions.length > 1) {
            toast.info(`Multiple transactions found for connector ${selectedConnector.connectorId}, stopping the first one`);
          }
          
          // Use RemoteStop with transaction ID
          const stopPayload = {
            parameters: {
              transactionId: connectorTransaction.transactionId
            },
            chargerId: chargerId,
            remoteOperationType: "RemoteStop",
            source: "FLEETOPS"
          };
          
          const response = await ApiService.post('/cms/v1/remote/operation', stopPayload);
          
          if (response.success) {
            toast.success(`Stopping transaction ${connectorTransaction.transactionId} on connector ${selectedConnector.connectorId}...`);
            
            // Check operation status and update connector if successful
            await checkOperationStatusAndUpdate(chargerId, response.data?.taskId);
            
            // Refresh parent data after successful operation
            if (onRefresh) {
              setTimeout(() => {
                onRefresh();
              }, 8000); // Refresh after 8 seconds
            }
          } else {
            toast.error(response.data?.message || response.message || 'Failed to stop transaction');
          }
        } else {
          console.log(`No transaction found for connector ${selectedConnector.connectorId}`);
          console.log('Available connector IDs in transactions:', transactions.map(t => t.connectorId));
          toast.error(`No active transaction found for connector ${selectedConnector.connectorId}`);
        }
      } else {
        toast.error('Failed to fetch active transactions');
      }
    } catch (error) {
      console.error('Error stopping charger:', error);
      toast.error('Error stopping charger');
    }
    onClose();
  };

  const handleReset = async () => {
    if (!selectedConnector) {
      toast.error('Please select a connector first');
      return;
    }
    try {
      const chargerId = charger.chargepoint_id || charger.id;
      const response = await ChargerService.resetCharger(chargerId, selectedConnector.connectorId);
      if (response.success) {
        toast.success(`Resetting connector ${selectedConnector.connectorId} on ${chargerId}...`);
        
        // Check operation status and update connector if successful
        await checkOperationStatusAndUpdate(chargerId, response.data?.taskId);
        
        // Refresh parent data after successful operation
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 4000); // Refresh after 4 seconds
        }
      } else {
        toast.error(response.data?.message || response.message || 'Failed to reset charger');
      }
    } catch (error) {
      console.error('Error resetting charger:', error);
      toast.error('Error resetting charger');
    }
    onClose();
  };

  const getConnectorStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return '#4caf50';
      case 'charging':
        return '#2196f3';
      case 'unavailable':
        return '#f44336';
      case 'preparing':
        return '#4caf50';
      case 'faulted':
        return '#d32f2f';
      case 'in use':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const formatHeartbeatTime = (timestamp) => {
    if (!timestamp) return 'No recent heartbeat';

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return 'No recent heartbeat';

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return date.toLocaleString('en-IN', {
      timeZone: userTimeZone,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchRfidTags = async () => {
    if (!selectedTeam?.teamId) {
      toast.error('No team selected');
      return;
    }

    const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    const userEmail = userProfile?.data?.email;
    
    if (!userEmail) {
      toast.error('User email not found');
      return;
    }

    setTagsLoading(true);
    try {
      const chargerId = charger.chargepoint_id || charger.id;
      const response = await ChargerService.getRfidTags(selectedTeam.teamId, chargerId, {
        email: userEmail
      });
      
      
      if (response.success && response.data && Array.isArray(response.data.data)) {
        setRfidTags(response.data.data);
        setShowTagSelection(true);
      } else {
        toast.error(response.message || 'Failed to fetch RFID tags');
      }
    } catch (error) {
      console.error('Error fetching RFID tags:', error);
      toast.error('Error fetching RFID tags');
    } finally {
      setTagsLoading(false);
    }
  };

  const checkOperationStatusAndUpdate = async (chargerId, taskId) => {
    if (!taskId) {
      console.log('No task ID provided for status check');
      return;
    }

    try {
      // Wait a bit for the operation to process
      setTimeout(async () => {
        try {
          const statusResponse = await ChargerService.getOperationStatus(chargerId, taskId);
          
          if (statusResponse.status === 'success') {
            toast.success(statusResponse.message || 'Operation performed successfully');
            
            // Update connector information
            if (selectedTeam?.teamId) {
              const connectorsResponse = await ChargerService.getChargerConnectors(selectedTeam.teamId, chargerId);
              
              if (connectorsResponse.success && connectorsResponse.data) {
                // Update the charger's connectors with new status
                const updatedConnectors = connectorsResponse.data.map(conn => ({
                  connector_pk: conn.connector_pk,
                  connectorId: conn.connector_id,
                  connectorName: conn.connectorName,
                  connectorType: conn.connectorType,
                  status: conn.status
                }));
                
                
                // Find and update the selected connector
                const updatedConnector = updatedConnectors.find(
                  conn => conn.connectorId === selectedConnector?.connectorId
                );
                
                
                if (updatedConnector) {
                  // Update the selected connector with new status
                  setSelectedConnector(prev => ({
                    ...prev,
                    ...updatedConnector
                  }));
                  
                  // Also update the charger's connectors array if needed
                  // if (charger && charger.connectors) {
                  //   const updatedChargerConnectors = charger.connectors.map(conn => {
                  //     const updated = updatedConnectors.find(u => u.connectorId === conn.connectorId);
                  //     return updated ? { ...conn, ...updated } : conn;
                  //   });
                    
                  // }
                  
                  toast.info(`Connector ${updatedConnector.connectorId} status updated to: ${updatedConnector.status}`);
                } else {
                  console.log('Could not find matching connector for update');
                }
              } else {
                console.log('Failed to get updated connector data:', connectorsResponse);
              }
            } else {
              console.log('No team ID available for connector update');
            }
          } else {
            toast.warning('Operation status check completed');
          }
        } catch (innerError) {
          console.error('Error in status check inner function:', innerError);
        }
      }, 3000); // Wait 3 seconds before checking status (increased from 2 seconds)
    } catch (error) {
      console.error('Error checking operation status:', error);
    }
  };
  
  const session = activeSessions.find(s => s.charger === charger?.id);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '425px',
          mx: 'auto',
          maxHeight: '80vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        p: 2,
        maxHeight: 'calc(80vh - 32px)',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        '&': {
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none'
        }
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="600">
              {charger?.chargepoint_id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {charger?.chargepoint_name}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Connector Selection */}
        {charger?.connectors && charger.connectors.length > 1 ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                Select Connector
              </Typography>
              <Grid container spacing={1}>
                {charger.connectors.map((connector) => (
                  <Grid item xs={4} key={connector.connector_pk}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: selectedConnector?.connector_pk === connector.connector_pk 
                          ? '2px solid' 
                          : '1px solid',
                        borderColor: selectedConnector?.connector_pk === connector.connector_pk 
                          ? 'primary.main' 
                          : 'divider',
                        bgcolor: selectedConnector?.connector_pk === connector.connector_pk 
                          ? 'primary.50' 
                          : 'background.paper'
                      }}
                      onClick={() => setSelectedConnector(connector)}
                    >
                      <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight="600">
                          {connector.connectorName || `C${connector.connectorId}`}
                        </Typography>
                        <Box 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: getConnectorStatusColor(connector.status),
                            mx: 'auto',
                            mt: 0.5
                          }} 
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {connector.status}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              label={`${charger.charger_type} • ${charger.ocpp_protocol}`}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}

        {/* Selected Connector Status */}
        {selectedConnector && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              label={`Connector ${selectedConnector.connectorId} (${selectedConnector.connectorName}) - ${selectedConnector.status}`}
              sx={{
                bgcolor: getConnectorStatusColor(selectedConnector.status),
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
        )}



        {/* Real-time Metrics - Only show if connector is selected */}
        {selectedConnector && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ActivityIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="600">
                  Connector {selectedConnector.connectorId} Status
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Connector Type
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {selectedConnector.connectorType || 'Type 2'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status
                    </Typography>
                    <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>
                      {selectedConnector.status}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Heartbeat
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatHeartbeatTime(charger.lastHeartbeatTimestamp)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Capacity
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {selectedConnector?.kw} kW
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Location
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {charger.city}, {charger.state}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Active Session - Only show if charging */}
        {charger.status === 'charging' && session && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BoltIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="600">
                  Active Session
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Vehicle</Typography>
                <Typography variant="body2" fontWeight="600">{session.vehicle}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Duration</Typography>
                <Typography variant="body2" fontWeight="600">{session.duration}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">State of Charge</Typography>
                <Chip 
                  size="small"
                  variant="outlined"
                  icon={<BatteryIcon />}
                  label={`${session.soc}%`}
                />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
                <Typography variant="body2" fontWeight="600">
                  KES {Math.round(session.energy * 50)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Charger Specifications */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SpeedIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="600">
                Charger Specifications
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Typography variant="body2" fontWeight="500">
                {charger.charger_type} Charger
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Protocol</Typography>
              <Typography variant="body2" fontWeight="500">{charger.ocpp_protocol}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Registration</Typography>
              <Typography variant="body2" fontWeight="500">
                {charger.registration_status}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Connectors</Typography>
              <Typography variant="body2" fontWeight="500">
                {charger.connectors?.length || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Location</Typography>
              <Typography variant="body2" fontWeight="500">
                {charger.city}, {charger.state}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* RFID Tag Selection - Show if connector is available or preparing */}
        {selectedConnector && (selectedConnector.status?.toLowerCase() === 'available' || selectedConnector.status?.toLowerCase() === 'preparing') && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                Select RFID Tag
              </Typography>
              
              {!showTagSelection ? (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={fetchRfidTags}
                  disabled={tagsLoading}
                  sx={{ mb: 2 }}
                >
                  {tagsLoading ? 'Loading Tags...' : 'Load RFID Tags'}
                </Button>
              ) : (
                <Box>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select RFID Tag</InputLabel>
                    <Select
                      value={selectedTag?.id || ''}
                      label="Select RFID Tag"
                      onChange={(e) => {
                        const tag = rfidTags.find(t => t.id === e.target.value);
                        setSelectedTag(tag || null);
                      }}
                    >
                      {rfidTags.map((tag) => (
                        <MenuItem key={tag.id} value={tag.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {tag.tagId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {tag.id}
                              </Typography>
                            </Box>
                            <Box>
                              {tag.balanceAmount ? (
                                <Chip 
                                  size="small"
                                  label={`₹${tag.balanceAmount}`}
                                  color="success"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip 
                                  size="small"
                                  label="No Balance"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {selectedTag && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        Selected: <strong>{selectedTag.tagId}</strong>
                      </Typography>
                      {selectedTag.balanceAmount ? (
                        <Chip 
                          size="small"
                          label={`₹${selectedTag.balanceAmount}`}
                          color="success"
                          variant="filled"
                        />
                      ) : (
                        <Chip 
                          size="small"
                          label="No Balance"
                          color="warning"
                          variant="filled"
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions - Only show if connector is selected */}
        {selectedConnector ? (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                Connector {selectedConnector.connectorId} Actions
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  {selectedConnector.status?.toLowerCase() === 'available' || selectedConnector.status?.toLowerCase() === 'preparing' ? (
                    <Button 
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<PlayIcon />}
                      onClick={handleRemoteStart}
                      disabled={!selectedTag}
                      sx={{ height: 48, color: '#fff', }}
                    >
                      {selectedTag ? 'Start Charging' : 'Select RFID Tag First'}
                    </Button>
                  ) : (selectedConnector.status?.toLowerCase() === 'charging' || selectedConnector.status?.toLowerCase() === 'in use') ? (
                    <Button 
                      variant="contained"
                      color="error"
                      fullWidth
                      size="large"
                      startIcon={<StopIcon />}
                      onClick={handleRemoteStop}
                      sx={{ height: 48 }}
                    >
                      Stop
                    </Button>
                  ) : (
                    <Button 
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<WarningIcon />}
                      disabled
                      sx={{ height: 48 }}
                    >
                      {selectedConnector.status}
                    </Button>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<PowerIcon />}
                    onClick={handleReset}
                    sx={{ height: 48 }}
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {charger.connectors?.length > 1 
                  ? 'Please select a connector above to view details and controls'
                  : 'No connectors available'
                }
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Drawer>
  );
};

export default ChargerDetailsDrawer;